import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  AlertTriangle,
  PlugZap,
  XCircle,
  Loader2,
  Store,
  Rocket,
  Truck,
  GraduationCap,
  Building2,
  KeyRound,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_ICONS = {
  store: Store,
  affiliate: Rocket,
  supplies: Truck,
  training: GraduationCap,
  partner: Store,
  franchise: Building2,
};

const STATUS_STYLES = {
  operational: {
    label: "Operational",
    icon: CheckCircle2,
    chip: "bg-lifted-green/15 text-lifted-green border-lifted-green/30",
    ring: "border-lifted-green/30",
  },
  needs_configuration: {
    label: "Needs configuration",
    icon: AlertTriangle,
    chip: "bg-amber-500/15 text-amber-200 border-amber-400/30",
    ring: "border-amber-400/30",
  },
  connection_required: {
    label: "Connection required",
    icon: PlugZap,
    chip: "bg-sky-500/15 text-sky-200 border-sky-400/30",
    ring: "border-sky-400/30",
  },
  error: {
    label: "Error",
    icon: XCircle,
    chip: "bg-red-500/15 text-red-200 border-red-400/30",
    ring: "border-red-400/30",
  },
};

export default function EcosystemReadinessWidget() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/ecosystem-readiness`, {
          credentials: "include",
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (alive) setData(json);
      } catch (e) {
        if (alive) setError(e.message || "Failed to load readiness");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div
        className="glass-panel p-6 flex items-center gap-2 text-lifted-dim"
        data-testid="ecosystem-readiness-loading"
      >
        <Loader2 className="animate-spin" size={16} /> Probing ecosystem readiness…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="glass-panel p-6 text-red-200"
        data-testid="ecosystem-readiness-error"
      >
        Could not load readiness ({error || "no data"}). Ensure you are signed in
        as master admin.
      </div>
    );
  }

  const modules = data.modules || [];
  const missing = data.missing_credentials || [];

  return (
    <section className="space-y-4" data-testid="ecosystem-readiness-widget">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">First login · Ecosystem readiness</div>
          <h2 className="font-anton uppercase text-3xl text-white">
            System status at a glance
          </h2>
          <p className="text-sm text-lifted-dim mt-1">
            {data.operational_count}/{data.total_count} subsystems operational ·
            last checked{" "}
            <span className="text-lifted-green">
              {new Date(data.evaluated_at).toLocaleString()}
            </span>
          </p>
        </div>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="ecosystem-readiness-grid"
      >
        {modules.map((m) => {
          const style = STATUS_STYLES[m.status] || STATUS_STYLES.error;
          const Icon = MODULE_ICONS[m.key] || Store;
          const StatusIcon = style.icon;
          return (
            <Link
              key={m.key}
              to={m.link}
              className={`glass-panel p-5 border ${style.ring} hover:-translate-y-1 transition-transform`}
              data-testid={`readiness-card-${m.key}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl border border-white/10 bg-white/5 text-white flex items-center justify-center">
                    <Icon size={18} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-lifted-dim">
                      {m.key} status
                    </div>
                    <div className="font-anton text-2xl uppercase text-white">
                      {m.label}
                    </div>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${style.chip}`}
                  data-testid={`readiness-status-${m.key}`}
                >
                  <StatusIcon size={12} />
                  {style.label}
                </span>
              </div>

              <ul className="mt-4 space-y-1.5">
                {(m.checks || []).map((c, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-xs"
                    data-testid={`readiness-check-${m.key}-${i}`}
                  >
                    {c.ok ? (
                      <CheckCircle2
                        size={13}
                        className="text-lifted-green mt-0.5 shrink-0"
                      />
                    ) : (
                      <XCircle
                        size={13}
                        className="text-red-300 mt-0.5 shrink-0"
                      />
                    )}
                    <span className={c.ok ? "text-white/80" : "text-red-100"}>
                      <span className="font-medium">{c.name}</span>
                      {c.detail ? (
                        <span className="text-lifted-dim"> — {c.detail}</span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>

              {m.missing_credentials && m.missing_credentials.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-wider text-amber-200/80 mb-1">
                    Missing credentials
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {m.missing_credentials.map((k) => (
                      <code
                        key={k}
                        className="text-[10px] bg-amber-500/10 text-amber-100 border border-amber-400/20 px-1.5 py-0.5 rounded"
                        data-testid={`readiness-missing-${m.key}-${k}`}
                      >
                        {k}
                      </code>
                    ))}
                  </div>
                </div>
              )}
            </Link>
          );
        })}
      </div>

      {missing.length > 0 && (
        <div
          className="glass-panel p-5 flex items-start gap-3 border border-amber-400/20"
          data-testid="ecosystem-readiness-missing-callout"
        >
          <KeyRound className="text-amber-300 mt-0.5 shrink-0" size={18} />
          <div className="flex-1">
            <div className="font-bebas tracking-wider text-white text-lg uppercase">
              Credentials required for full operation
            </div>
            <p className="text-xs text-lifted-dim mb-2">
              Add these to <code className="text-white">/app/backend/.env</code>{" "}
              and restart the backend. The app stays operational until they're
              set.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {missing.map((k) => (
                <code
                  key={k}
                  className="text-xs bg-amber-500/10 text-amber-100 border border-amber-400/20 px-2 py-0.5 rounded"
                  data-testid={`readiness-missing-${k}`}
                >
                  {k}
                </code>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
